import { useCallback, useEffect, useState } from 'react';
import type { PatternBased, RuleBased, RuleSet, RuleVersionSummary } from '@kkp-fraud/shared';
import { api, type ApiSettings } from '../api';
import { isDemoVersion } from '../demo';
import type { RuleKind } from '../hooks/useRuleSetDraft';
import { t } from '../i18n';
import { findRule, sameRule } from '../scopedPublish';
import { ActionConditionList } from './ActionConditionList';
import { Dialog } from './Dialog';
import { When } from './When';

/**
 * One rule across the document's released versions — the row menu's "Version history".
 * Each version is an immutable snapshot, so a fetched one is kept for the session.
 * "Restore to draft" only edits the draft; releasing it is the ordinary release path.
 */

type AnyRule = RuleBased | PatternBased;

const versionCache = new Map<string, RuleSet>();

/**
 * Versions read per page. Every version is its own document, and a live rule set gains one per
 * release (55+ on the POC by 30 Sep), so reading all of them at once made the dialog slower with
 * every release. One extra (older) document is read past the page so its last row can still say
 * whether the rule changed there.
 */
export const HISTORY_PAGE = 20;

/** Tests serve different snapshots under the same version names. */
export function resetRuleHistoryCacheForTests(): void {
  versionCache.clear();
}

async function snapshot(settings: ApiSettings, version: string): Promise<RuleSet> {
  const key = `${settings.baseUrl}|${version}`;
  const hit = versionCache.get(key);
  if (hit) return hit;
  const res = await api.version(settings, version);
  versionCache.set(key, res.ruleSet);
  return res.ruleSet;
}

interface Row {
  summary: RuleVersionSummary;
  rule: AnyRule | undefined;
  mark: 'changed' | 'added' | 'absent' | null;
}

function rowsOf(versions: RuleVersionSummary[], docs: RuleSet[], kind: RuleKind, id: string): Row[] {
  const rules = docs.map((doc) => findRule(doc, kind, id));
  return versions.map((summary, i) => {
    const rule = rules[i];
    const hasOlder = i + 1 < versions.length;
    const older = rules[i + 1];
    const mark: Row['mark'] = !rule ? 'absent' : hasOlder && !older ? 'added' : hasOlder && !sameRule(older, rule) ? 'changed' : null;
    return { summary, rule, mark };
  });
}

export function RuleHistoryDialog({
  kind,
  id,
  settings,
  onRestore,
  onClose,
}: {
  kind: RuleKind;
  id: string;
  settings: ApiSettings;
  /** Copy an older version of the rule into the draft. Absent (a Viewer) → no Restore button. */
  onRestore?: (rule: AnyRule, version: string) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState<RuleVersionSummary[] | null>(null);
  const [shown, setShown] = useState(HISTORY_PAGE);
  const [loadingMore, setLoadingMore] = useState(false);

  /** The newest `count` versions as rows (plus one older document for the last row's mark). */
  const load = useCallback(
    async (newestFirst: RuleVersionSummary[], count: number) => {
      const page = newestFirst.slice(0, Math.min(count + 1, newestFirst.length));
      const docs = await Promise.all(page.map((v) => snapshot(settings, v.version)));
      return rowsOf(page, docs, kind, id).slice(0, count);
    },
    [settings, kind, id],
  );

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { versions } = await api.versions(settings);
        const newestFirst = [...versions].sort((a, b) => Date.parse(b.savedUtc) - Date.parse(a.savedUtc));
        const first = await load(newestFirst, HISTORY_PAGE);
        if (live) {
          setAll(newestFirst);
          setShown(HISTORY_PAGE);
          setRows(first);
        }
      } catch (e) {
        if (live) setError((e as Error).message);
      }
    })();
    return () => {
      live = false;
    };
  }, [settings, load]);

  const more = async () => {
    if (!all) return;
    setLoadingMore(true);
    try {
      const next = shown + HISTORY_PAGE;
      setRows(await load(all, next));
      setShown(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Dialog title={t('row.history.title', { id })} wide plainBody onClose={onClose}>
      {error && (
        <div className="banner error" role="status">
          {error}
        </div>
      )}
      {!error && !rows && (
        <div className="empty" role="status">
          {t('versions.loading.label')}
        </div>
      )}
      {rows && (
        <div className="tbl-wrap">
          <table className="fixed" data-testid="rule-history-table">
            <colgroup>
              <col style={{ width: 112 }} />
              <col style={{ width: 112 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: '18%' }} />
              <col />
              <col style={{ width: 150 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('versions.col.version')}</th>
                <th>{t('versions.col.saved')}</th>
                <th>{t('versions.col.actor')}</th>
                <th>{t('versions.col.comment')}</th>
                <th>{t('col.actionCondition.label')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ summary: v, rule, mark }) => (
                <tr key={v.version} data-testid={`rule-history-${v.version}`} className={rule ? undefined : 'disabled'}>
                  <td className="mono">
                    <span className="nowrap">{v.version}</span>{' '}
                    {v.isActive && (
                      <span className="chip ok" data-testid={`rule-history-active-${v.version}`}>
                        {t('versions.active.label')}
                      </span>
                    )}
                  </td>
                  <td className="small nowrap">
                    <When iso={v.savedUtc} />
                  </td>
                  <td className="small">{v.actor}</td>
                  <td className="small">{v.comment ?? '—'}</td>
                  <td>
                    {mark && (
                      <div className="row-wrap" style={{ gap: 4, marginBottom: rule ? 6 : 0 }}>
                        {mark === 'changed' && (
                          <span className="chip warn" data-testid={`rule-history-mark-${v.version}`}>
                            {t('row.history.changed')}
                          </span>
                        )}
                        {mark === 'added' && (
                          <span className="chip info" data-testid={`rule-history-mark-${v.version}`}>
                            {t('row.history.added')}
                          </span>
                        )}
                        {mark === 'absent' && (
                          <span className="chip mute" data-testid={`rule-history-mark-${v.version}`}>
                            {t('row.history.absent')}
                          </span>
                        )}
                      </div>
                    )}
                    {rule && <ActionConditionList rule={rule} testid={`rule-history-actions-${v.version}`} />}
                  </td>
                  <td className="nowrap">
                    {onRestore && !v.isActive && rule && !isDemoVersion(v.version) && (
                      <button type="button" className="btn" data-testid={`rule-history-restore-${v.version}`} onClick={() => onRestore(rule, v.version)}>
                        {t('row.history.restore')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {all && all.length > shown && (
            <div className="row-wrap" style={{ justifyContent: 'center', marginTop: 12 }}>
              <button type="button" className="btn" data-testid="rule-history-more" disabled={loadingMore} onClick={more}>
                {loadingMore ? t('versions.loading.label') : t('row.history.more', { n: Math.min(HISTORY_PAGE, all.length - shown), total: all.length })}
              </button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
